import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, useMediaQuery, Theme, Button, IconButton, Drawer, CircularProgress } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from './components/Toolbar';
import MainInput from './components/MainInput';
import AIResponsePanel, { AIResponse } from './components/AIResponsePanel';
import SettingsPage from './pages/SettingsPage';
import { useSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector, toggleTheme, setLoading as setConversationsLoading, setConversations, setError as setConversationsError, addConversation, setActiveConversation, updateConversation, removeConversation, setSearchResults } from './store';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { io, Socket } from 'socket.io-client';
import ConversationPanel from './components/ConversationPanel';
import AudioPanel from './components/AudioPanel';
import { v4 as uuidv4 } from 'uuid';

// Sidebar components
import TopActions from './components/TopActions';
import ConversationHistoryList from './components/ConversationHistoryList';
import BottomSection from './components/BottomSection';

// API endpoints
import { ENDPOINTS, WS_NAMESPACES, getConversationEndpoint } from './config/api';

// Import auth utilities
import { getJwtFromCookie, isAuthenticated, redirectToAuth } from './utils/auth';

const PreviewImg = styled('img')({
  maxWidth: '100%',
  maxHeight: 120,
  borderRadius: 8,
  margin: '8px 0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
});

import RequireAuth from './components/RequireAuth';
import PropertyAnalysisPanel from './components/property/PropertyAnalysisPanel';
import FloatingProgressWidget from './components/property/FloatingProgressWidget';
import AnalysisStatusBar from './components/property/AnalysisStatusBar';
import LiveActivityFeed from './components/property/LiveActivityFeed';
import TradingProgressModal from './components/TradingProgressModal';
import TradingFAB from './components/TradingFAB';

const App: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [responses, setResponses] = useState<AIResponse[]>([]);
  const [showAIResponse, setShowAIResponse] = useState(true);
  const [screenImage, setScreenImage] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const themeMode = useAppSelector((state) => state.theme);
  const activeConversationId = useAppSelector((state) => state.conversations.activeConversationId);
  const activeConversation = useAppSelector((state) => activeConversationId ? state.conversations.items[activeConversationId] : null);
  const activeAnalysis = useAppSelector((state) => state.propertyAnalysis?.activeAnalysis);
  
  console.log('App: activeConversationId:', activeConversationId);
  console.log('App: activeConversation:', activeConversation);
  const handleThemeToggle = () => dispatch(toggleTheme());
  const handleOpenSettings = useCallback(() => setShowSettings(true), []);
  const handleCloseSettings = useCallback(() => setShowSettings(false), []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamPreview, setStreamPreview] = useState<string | null>(null);
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const socketRef = React.useRef<Socket | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const streamIntervalRef = React.useRef<any>(null);
  const [aiAnalysisEnabled, setAiAnalysisEnabled] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<string>('');
  const [transcriptions, setTranscriptions] = useState<Array<{
    text: string;
    confidence: number;
    timestamp: number;
  }>>([]);
  const [audioAnalysis, setAudioAnalysis] = useState<Array<{
    analysis: string;
    insights: string[];
    actions: string[];
    confidence: number;
    timestamp: number;
  }>>([]);
  const [aiAnalysisInterval, setAiAnalysisInterval] = useState(2000); // default 2s
  const aiAnalysisIntervalRef = React.useRef<any>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const staticPreviewCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [isMicStreaming, setIsMicStreaming] = useState(false);
  const micStreamRef = React.useRef<MediaStream | null>(null);
  const micRecorderRef = React.useRef<MediaRecorder | null>(null);
  const micSocketRef = React.useRef<Socket | null>(null);
  const [micVolume, setMicVolume] = useState(0);
  const micVolumeAnimationRef = React.useRef<number | null>(null);
  const micVolumeRunningRef = React.useRef(false);
  const [audioSource, setAudioSource] = useState<'mic' | 'system' | 'both'>('mic');
  const micRecordingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const [showTranscription, setShowTranscription] = useState(false);
  
  // New state for video source management
  const [videoSource, setVideoSource] = useState<'screen' | 'webcam'>('screen');
  const [isWebcamStreaming, setIsWebcamStreaming] = useState(false);
  const webcamStreamRef = React.useRef<MediaStream | null>(null);
  
  // Floating progress widget state
  const [showFloatingWidget, setShowFloatingWidget] = useState(true);
  const [showStatusBar, setShowStatusBar] = useState(true);
  const [showActivityFeed, setShowActivityFeed] = useState(true);
  
  // New state for webcam device selection
  const [availableWebcams, setAvailableWebcams] = useState<MediaDeviceInfo[]>([]);
  const [selectedWebcamId, setSelectedWebcamId] = useState<string>('');

  // Ref to track if we're in initial stream setup to prevent source switching conflicts
  const isInitialStreamSetupRef = React.useRef(false);
  
  // State to track when source switching is in progress
  const [isSourceSwitching, setIsSourceSwitching] = useState(false);

  // State to track when webcam device switching is in progress for Start Webcam button
  const [isWebcamDeviceSwitching, setIsWebcamDeviceSwitching] = useState(false);

  // Toolbar orientation and side state
  const [toolbarOrientation, setToolbarOrientation] = useState<'horizontal' | 'vertical' | 'bottom'>('horizontal');
  const [toolbarSide, setToolbarSide] = useState<'left' | 'right'>('left');

  // Sidebar API integration - using existing dispatch
  // const dispatch = useAppDispatch();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const focusSearchInput = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load user settings on component mount
  React.useEffect(() => {
    const loadUserSettings = async () => {
      try {
        // Check if user is authenticated
        if (!isAuthenticated()) {
          console.log('User not authenticated, redirecting to auth');
          redirectToAuth();
          return;
        }

        const jwt = getJwtFromCookie();
        if (!jwt) {
          console.log('No JWT found, redirecting to auth');
          redirectToAuth();
          return;
        }
        
        const response = await fetch(ENDPOINTS.USERS_ME, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.status === 401) {
          console.log('Unauthorized, redirecting to auth');
          redirectToAuth();
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          const userData = data.data || data;
          
          // Set toolbar orientation from user settings
          if (userData.settings?.toolbarLayout) {
            setToolbarOrientation(userData.settings.toolbarLayout);
          }
        }
      } catch (error) {
        console.error('Failed to load user settings:', error);
        // On error, redirect to auth
        redirectToAuth();
      }
    };
    
    loadUserSettings();
  }, []);

  // Keyboard shortcut: Cmd+, to open settings
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        setShowSettings(true);
        enqueueSnackbar('Opened settings (Cmd+,)', { variant: 'info', autoHideDuration: 1500 });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enqueueSnackbar]);

  // Create a new conversation if none exists
  const createConversation = async (
    type: 'general' | 'screenshare' = 'general',
    title?: string
  ): Promise<string> => {
    const jwt = getJwtFromCookie();
    if (!jwt) throw new Error('No JWT token found');
    const conversationTitle =
      type === 'screenshare'
        ? 'Screen Share Session'
        : title && title.trim().length > 0
        ? title.length > 50
          ? title.substring(0, 50) + '...'
          : title
        : 'New Conversation';
    const res = await fetch(ENDPOINTS.CONVERSATIONS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        title: conversationTitle,
        type,
      }),
      credentials: 'include',
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to create conversation');
    return data.data.id;
  };

  // Utility to upload a base64 image to S3 and return the URL
  async function uploadImageToS3(imageDataUrl: string): Promise<string | undefined> {
    try {
      const res = await fetch(imageDataUrl);
      const blob = await res.blob();
      // Use a descriptive, unique filename for each upload
      const timestamp = Date.now();
      let userId = '';
      try {
        const jwt = getJwtFromCookie();
        if (jwt) {
          const payload = JSON.parse(atob(jwt.split('.')[1]));
          userId = payload.userId || payload.sub || '';
        }
      } catch {}
      const uuid = uuidv4();
      const fileName = userId
        ? `capture-${userId}-${timestamp}-${uuid}.png`
        : `capture-${timestamp}-${uuid}.png`;
      const file = new File([blob], fileName, { type: blob.type });
      const formData = new FormData();
      formData.append('image', file);
      const jwt = getJwtFromCookie();
      const uploadRes = await fetch(ENDPOINTS.MEDIA_UPLOAD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        body: formData,
        credentials: 'include',
      });
      const data = await uploadRes.json();
      return data.success ? data.url : undefined;
    } catch (err) {
      enqueueSnackbar('Image upload failed', { variant: 'error' });
      return undefined;
    }
  }

  // REST API integration for submitting questions
  const handleSubmit = async (question: string) => {
    console.log('🔍 handleSubmit called with question:', question);
    setLoading(true);
    try {
      const jwt = getJwtFromCookie();
      console.log('🔍 JWT token exists:', !!jwt);
      if (!jwt) {
        throw new Error('No authentication token found. Please log in.');
      }

      // Always create a new conversation for each query submission, using the question as the title
      console.log('🔍 Creating new conversation for query...');
      const currentConversationId = await createConversation('general', question);
      console.log('🔍 New conversation created:', currentConversationId);
      dispatch(setActiveConversation(currentConversationId));
      // Don't show conversation panel for new conversations created by submitting queries
      setShowConversationPanel(false);

      // Create user message with the question
      console.log('🔍 Creating user message...');
      const userMessageRes = await fetch(getConversationEndpoint(currentConversationId) + '/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          type: 'user',
          content: question,
        }),
        credentials: 'include',
      });
      console.log('🔍 User message creation response status:', userMessageRes.status);
      const userMessageData = await userMessageRes.json();
      console.log('🔍 User message creation response:', userMessageData);

      // Update conversation title with the question (truncate if too long)
      const title = question.length > 50 ? question.substring(0, 50) + '...' : question;
      console.log('🔍 Updating conversation title to:', title);
      const titleUpdateRes = await fetch(getConversationEndpoint(currentConversationId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ title }),
        credentials: 'include',
      });
      console.log('🔍 Title update response status:', titleUpdateRes.status);
      if (titleUpdateRes.ok) {
        const titleUpdateData = await titleUpdateRes.json();
        console.log('🔍 Title update response:', titleUpdateData);
        if (titleUpdateData.success) {
          dispatch(updateConversation(titleUpdateData.data));
        }
      }

      let aiResp: AIResponse | null = null;
      let imageUrl: string | undefined = undefined;
      let s3UploadPromise: Promise<string | undefined> | undefined;
      let imageToUpload: string | undefined = undefined;
      let messageId: string | null = null;
      
      // If a static screen capture exists, upload to S3 in parallel
      if (screenImage) {
        console.log('🔍 Processing screen image...');
        imageToUpload = screenImage;
        s3UploadPromise = uploadImageToS3(screenImage);
        const res = await fetch(ENDPOINTS.AI_ANALYZE_SCREEN, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            imageData: screenImage,
            prompt: question,
            context: { conversationId: currentConversationId }
          }),
          credentials: 'include',
        });
        console.log('🔍 AI analyze-screen response status:', res.status);
        const data = await res.json();
        console.log('🔍 AI analyze-screen response:', data);
        if (!data.success) throw new Error(data.error || 'AI analysis failed');
        const analysisText = data.data?.analysis || data.data?.response || 'No analysis available';
        
        // Wait for S3 upload to complete if it was started
        let imageUrl: string | undefined = undefined;
        if (s3UploadPromise) {
          try {
            imageUrl = await s3UploadPromise;
            console.log('🔍 S3 upload completed:', imageUrl);
          } catch (error) {
            console.error('🔍 S3 upload failed:', error);
          }
        }

        // Create a message in the database for this AI response
        console.log('🔍 Creating message in database...');
        const messageRes = await fetch(getConversationEndpoint(currentConversationId) + '/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: 'ai',
            aiResponse: { 
              content: analysisText,
              image: imageUrl
            },
            metadata: {
              aiModel: 'google/gemini-2.5-pro',
              processingTime: Date.now(),
              source: 'screen-analysis'
            }
          }),
          credentials: 'include',
        });
        console.log('🔍 Message creation response status:', messageRes.status);
        const messageData = await messageRes.json();
        console.log('🔍 Message creation response:', messageData);
        if (messageData.success) {
          messageId = messageData.data._id;
          console.log('🔍 Message ID created:', messageId);
        }
        
        aiResp = {
          id: messageId ? parseInt(messageId) : Date.now(),
          content: analysisText,
          image: imageUrl, // Add image URL for AIResponsePanel
        };
        console.log('🔍 AI response created:', aiResp);
        setScreenImage(null);
      // If live streaming and a stream preview exists, upload to S3 in parallel
      } else if (streamPreview) {
        imageToUpload = streamPreview;
        s3UploadPromise = uploadImageToS3(streamPreview);
        const res = await fetch(ENDPOINTS.AI_ANALYZE_SCREEN, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            imageData: streamPreview,
            prompt: question,
            context: { conversationId: currentConversationId }
          }),
          credentials: 'include',
        });
        console.log('🔍 AI analyze-screen response status:', res.status);
        const data = await res.json();
        console.log('🔍 AI analyze-screen response:', data);
        if (!data.success) throw new Error(data.error || 'AI analysis failed');
        const analysisText = data.data?.analysis || data.data?.response || 'No analysis available';
        
        // Wait for S3 upload to complete if it was started
        let imageUrl: string | undefined = undefined;
        if (s3UploadPromise) {
          try {
            imageUrl = await s3UploadPromise;
            console.log('🔍 S3 upload completed:', imageUrl);
          } catch (error) {
            console.error('🔍 S3 upload failed:', error);
          }
        }

        // Create a message in the database for this AI response
        console.log('🔍 Creating message in database...');
        const messageRes = await fetch(getConversationEndpoint(currentConversationId) + '/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: 'ai',
            aiResponse: { 
              content: analysisText,
              image: imageUrl
            },
            metadata: {
              aiModel: 'google/gemini-2.5-pro',
              processingTime: Date.now(),
              source: 'stream-analysis'
            }
          }),
          credentials: 'include',
        });
        console.log('🔍 Message creation response status:', messageRes.status);
        const messageData = await messageRes.json();
        console.log('🔍 Message creation response:', messageData);
        if (messageData.success) {
          messageId = messageData.data._id;
          console.log('🔍 Message ID created:', messageId);
        }
        
        aiResp = {
          id: messageId ? parseInt(messageId) : Date.now(),
          content: analysisText,
          image: imageUrl, // Include image URL for sharing
        };
        console.log('🔍 AI response created:', aiResp);
      // If webcam streaming and a webcam stream exists, analyze the webcam
      } else if (isWebcamStreaming && webcamStreamRef.current) {
        console.log('🔍 Processing webcam stream...');
        // Capture a frame from the webcam video element
        let webcamImageUrl: string | undefined = undefined;
        let webcamDataUrl: string | undefined = undefined;
        if (webcamVideoRef && webcamVideoRef.current) {
          const video = webcamVideoRef.current;
          console.log('🔍 Webcam video element found:', {
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            readyState: video.readyState,
            paused: video.paused,
            srcObject: !!video.srcObject
          });
          
          // Wait for video to be ready if needed
          if (video.readyState < 2) {
            console.log('🔍 Waiting for webcam video to be ready...');
            await new Promise<void>((resolve) => {
              const onLoadedData = () => {
                video.removeEventListener('loadeddata', onLoadedData);
                resolve();
              };
              video.addEventListener('loadeddata', onLoadedData);
              // Timeout after 5 seconds
              setTimeout(() => {
                video.removeEventListener('loadeddata', onLoadedData);
                resolve();
              }, 5000);
            });
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            webcamDataUrl = canvas.toDataURL('image/png');
            console.log('🔍 Webcam frame captured, data URL length:', webcamDataUrl.length);
            // Upload to S3
            webcamImageUrl = await uploadImageToS3(webcamDataUrl);
            console.log('🔍 Webcam image uploaded to S3:', webcamImageUrl);
          } else {
            console.error('🔍 Failed to get canvas context for webcam frame capture');
          }
        } else {
          console.error('🔍 Webcam video element not found or not ready');
          // Fallback: try to use the main video element if it's a webcam stream
          if (videoRef && videoRef.current && videoSource === 'webcam') {
            console.log('🔍 Trying fallback to main video element for webcam analysis');
            const video = videoRef.current;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              webcamDataUrl = canvas.toDataURL('image/png');
              console.log('🔍 Webcam frame captured from main video, data URL length:', webcamDataUrl.length);
              // Upload to S3
              webcamImageUrl = await uploadImageToS3(webcamDataUrl);
              console.log('🔍 Webcam image uploaded to S3 (fallback):', webcamImageUrl);
            }
          }
        }
        const res = await fetch(ENDPOINTS.AI_ANALYZE_SCREEN, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            imageData: webcamDataUrl,
            prompt: question,
            context: { conversationId: currentConversationId }
          }),
          credentials: 'include',
        });
        console.log('🔍 AI analyze-webcam response status:', res.status);
        const data = await res.json();
        console.log('🔍 AI analyze-webcam response:', data);
        if (!data.success) throw new Error(data.error || 'AI analysis failed');
        const analysisText = data.data?.analysis || data.data?.response || 'No analysis available';
        // Create a message in the database for this AI response
        console.log('🔍 Creating message in database...');
        const messageRes = await fetch(getConversationEndpoint(currentConversationId) + '/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: 'ai',
            aiResponse: { 
              content: analysisText,
              image: webcamImageUrl // Include webcam image URL
            },
            metadata: {
              aiModel: 'google/gemini-2.5-pro',
              processingTime: Date.now(),
              source: 'webcam-analysis'
            }
          }),
          credentials: 'include',
        });
        console.log('🔍 Message creation response status:', messageRes.status);
        const messageData = await messageRes.json();
        console.log('🔍 Message creation response:', messageData);
        if (messageData.success) {
          messageId = messageData.data._id;
          console.log('🔍 Message ID created:', messageId);
        }
        aiResp = {
          id: messageId ? parseInt(messageId) : Date.now(),
          content: analysisText,
          image: webcamImageUrl, // Include webcam image URL for sharing
        };
        console.log('🔍 AI response created:', aiResp);
      // If no image or stream, just send the question to the AI
      } else {
        console.log('🔍 Processing text-only question...');
        const res = await fetch(ENDPOINTS.AI_CHAT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            question,
            conversationId: currentConversationId,
          }),
          credentials: 'include',
        });
        console.log('🔍 AI chat response status:', res.status);
        const data = await res.json();
        console.log('🔍 AI chat response:', data);
        if (!data.success) throw new Error(data.error || 'AI chat failed');
        const responseText = data.data?.response || data.data?.content || 'No response available';
        
        // Create a message in the database for this AI response
        console.log('🔍 Creating message in database...');
        const messageRes = await fetch(getConversationEndpoint(currentConversationId) + '/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: 'ai',
            aiResponse: { 
              content: responseText,
              image: undefined // No image for text-only chat
            },
            metadata: {
              aiModel: 'google/gemini-2.5-pro',
              processingTime: Date.now(),
              source: 'text-chat'
            }
          }),
          credentials: 'include',
        });
        console.log('🔍 Message creation response status:', messageRes.status);
        const messageData = await messageRes.json();
        console.log('🔍 Message creation response:', messageData);
        if (messageData.success) {
          messageId = messageData.data._id;
          console.log('🔍 Message ID created:', messageId);
        }
        
        aiResp = {
          id: messageId ? parseInt(messageId) : Date.now(),
          content: responseText,
          image: undefined,
        };
        console.log('🔍 AI response created:', aiResp);
      }

      // Note: Image URLs are now handled within each analysis section
      // and saved directly to the database messages

      // Add the AI response to the responses array
      if (aiResp) {
        setResponses(prev => [...prev, aiResp!]);
        console.log('🔍 AI response added to responses array');
      }

      // Show success message
      enqueueSnackbar('AI analysis completed!', { variant: 'success' });
      console.log('🔍 handleSubmit completed successfully');

    } catch (error) {
      console.error('🔍 handleSubmit error:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to process request', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Temporary function to set JWT token for testing
  const setTestToken = () => {
    const testToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2ODVkOTZjYjg2NTRjNWI4NjQyZTJlOTEiLCJlbWFpbCI6InRoaXJkZXllYWlfdGVzdEBleGFtcGxlLmNvbSIsImlhdCI6MTc1MTA1MTAyOCwiZXhwIjoxNzUxMTM3NDI4fQ.Vlnbv3yrgYybNjhkK97O76hlnh7Y7BVw-6c3E6vjC00';
    localStorage.setItem('jwt', testToken);
    enqueueSnackbar('Test JWT token set!', { variant: 'success', autoHideDuration: 2000 });
  };

  // Screen capture handler
  const handleScreenCapture = async () => {
    try {
      // Request screen media
      const stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
      const track = stream.getVideoTracks()[0];
      const imageCapture = new (window as any).ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();
      // Draw to canvas
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(bitmap, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      setScreenImage(dataUrl);
      track.stop();
    } catch (err: any) {
      enqueueSnackbar('Screen capture failed: ' + (err.message || err), { variant: 'error' });
    }
  };

  // Webcam capture handler
  const handleWebcamCapture = async () => {
    try {
      // Request webcam media with selected device
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          deviceId: selectedWebcamId ? { exact: selectedWebcamId } : undefined
        } 
      });
      const track = stream.getVideoTracks()[0];
      const imageCapture = new (window as any).ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();
      // Draw to canvas
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(bitmap, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      setScreenImage(dataUrl);
      track.stop();
    } catch (err: any) {
      const errorMsg = err && typeof err === 'object' && 'message' in err ? err.message : String(err);
      enqueueSnackbar('Webcam capture failed: ' + errorMsg, { variant: 'error' });
    }
  };

  // Webcam streaming handler
  const handleWebcamStream = async () => {
    if (isWebcamStreaming) {
      setIsWebcamStreaming(false);
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach(track => track.stop());
        webcamStreamRef.current = null;
      }
      // Also stop the main video stream if it's using webcam
      if (videoSource === 'webcam' && streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
      enqueueSnackbar('Webcam stream stopped', { variant: 'info' });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          deviceId: selectedWebcamId ? { exact: selectedWebcamId } : undefined
        } 
      });
      webcamStreamRef.current = stream;
      setIsWebcamStreaming(true);
      
      // If video source is set to webcam, also update the main video stream
      if (videoSource === 'webcam') {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }
      
      enqueueSnackbar('Webcam stream started', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar('Failed to start webcam: ' + (err.message || err), { variant: 'error' });
    }
  };

  // Move this function to the top level of the component
  function startStreamingWithVideo({
    videoEl,
    jwt,
    aiAnalysisEnabled,
    aiAnalysisInterval,
    enqueueSnackbar,
    setLiveSessionId,
    setStreamPreview,
    setAiAnalysisResult,
    setIsStreaming,
    streamIntervalRef,
    aiAnalysisIntervalRef
  }: {
    videoEl: HTMLVideoElement;
    jwt: string;
    aiAnalysisEnabled: boolean;
    aiAnalysisInterval: number;
    enqueueSnackbar: any;
    setLiveSessionId: any;
    setStreamPreview: any;
    setAiAnalysisResult: any;
    setIsStreaming: any;
    streamIntervalRef: any;
    aiAnalysisIntervalRef: any;
  }) {
    const socket = io(WS_NAMESPACES.LIVE_SCREEN, {
      auth: { token: jwt },
      transports: ['websocket'],
    });
    socketRef.current = socket;
    socket.emit('create_session', { name: 'ScreenShare' });
    let sessionId: string | null = null;
    socket.on('session_created', ({ sessionId: newSessionId }) => {
      sessionId = newSessionId;
      setLiveSessionId(sessionId);
      enqueueSnackbar('Live stream started', { variant: 'success' });
      streamIntervalRef.current = setInterval(() => {
        if (videoEl.readyState === 4 && sessionId) {
          const canvas = document.createElement('canvas');
          canvas.width = videoEl.videoWidth;
          canvas.height = videoEl.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          socket.emit('frame', { sessionId, imageData: dataUrl });
          setStreamPreview(dataUrl);
        }
      }, 200);
      if (aiAnalysisEnabled) {
        aiAnalysisIntervalRef.current = setInterval(() => {
          if (videoEl.readyState === 4 && sessionId) {
            const canvas = document.createElement('canvas');
            canvas.width = videoEl.videoWidth;
            canvas.height = videoEl.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            socket.emit('analyze_frame', { sessionId, imageData: dataUrl });
          }
        }, aiAnalysisInterval);
      }
    });
    socket.on('analysis_result', (result) => {
      // Extract the human-readable analysis text
      const analysisText = result?.analysis || result?.response || 'No analysis available';
      setAiAnalysisResult(analysisText);
    });
    socket.on('error', (err) => {
      console.log('🚨 /live-screen WebSocket error:', err);
      console.trace('Error call stack');
      enqueueSnackbar('Live stream error: ' + (err?.error || err), { variant: 'error' });
      if (err?.error?.toLowerCase().includes('session')) {
        setIsStreaming(false);
        setLiveSessionId(null);
        setStreamPreview(null);
        setAiAnalysisResult('');
        if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
        if (aiAnalysisIntervalRef.current) clearInterval(aiAnalysisIntervalRef.current);
      }
    });
    socket.on('disconnect', () => {
      console.log('🚨 /live-screen WebSocket disconnected! Cleaning up stream state...');
      console.trace('Disconnect call stack');
      setIsStreaming(false);
      setStreamPreview(null);
      setLiveSessionId(null);
      setAiAnalysisResult('');
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
      if (aiAnalysisIntervalRef.current) clearInterval(aiAnalysisIntervalRef.current);
    });
  }

  // Start/stop live streaming with socket.io - now handles both screen and webcam
  const handleLiveStream = async () => {
    if (isStreaming) {
      console.log('🛑 Stopping live stream');
      setIsStreaming(false);
      if (socketRef.current) socketRef.current.disconnect();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
      if (aiAnalysisIntervalRef.current) {
        clearInterval(aiAnalysisIntervalRef.current);
        aiAnalysisIntervalRef.current = null;
      }
      setStreamPreview(null);
      setLiveSessionId(null);
      setAiAnalysisResult('');
      enqueueSnackbar('Live stream stopped', { variant: 'info' });
      return;
    }
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');
      
      // Set flag to indicate initial stream setup
      isInitialStreamSetupRef.current = true;
      console.log('🚀 Starting initial stream setup with source:', videoSource);
      
      let mediaStream: MediaStream;
      
      if (videoSource === 'screen') {
        // Screen capture
        mediaStream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
      } else {
        // Webcam capture
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 1280 },
            height: { ideal: 720 },
            deviceId: selectedWebcamId ? { exact: selectedWebcamId } : undefined
          } 
        });
      }
      
      streamRef.current = mediaStream;
      
      // Add event listeners to detect when stream tracks end
      mediaStream.getTracks().forEach(track => {
        track.addEventListener('ended', () => {
          console.log('🚨 Media stream track ended! Track kind:', track.kind);
          console.log('🚨 This might be due to switching to a Redfin tab or browser security restrictions');
          // Clean up the stream when tracks end
          setIsStreaming(false);
          if (socketRef.current) socketRef.current.disconnect();
          enqueueSnackbar('Screen sharing stopped - browser may have blocked the stream', { variant: 'warning' });
        });
      });
      
      setIsStreaming(true);
      // The rest of the setup will happen in useEffect below
    } catch (err: any) {
      console.error('❌ Failed to start live stream:', err);
      enqueueSnackbar('Failed to start live stream: ' + (err.message || err), { variant: 'error' });
      setIsStreaming(false);
      isInitialStreamSetupRef.current = false;
    }
  };

  // useEffect to set up video and start streaming only after videoRef.current is available
  React.useEffect(() => {
    if (isStreaming && videoRef.current && streamRef.current) {
      console.log('🎬 Setting up video element for initial stream');
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.onloadedmetadata = () => {
        console.log('UI Video loadedmetadata, videoWidth:', videoRef.current?.videoWidth, 'videoHeight:', videoRef.current?.videoHeight);
        videoRef.current?.play();
        // Now start streaming logic
        const jwt = getJwtFromCookie() || '';
        startStreamingWithVideo({
          videoEl: videoRef.current!,
          jwt,
          aiAnalysisEnabled,
          aiAnalysisInterval,
          enqueueSnackbar,
          setLiveSessionId,
          setStreamPreview,
          setAiAnalysisResult,
          setIsStreaming,
          streamIntervalRef,
          aiAnalysisIntervalRef
        });
        // Clear initial setup flag after stream is established
        isInitialStreamSetupRef.current = false;
        console.log('✅ Initial stream setup complete, flag cleared');
      };
      videoRef.current.onerror = (e) => {
        console.error('UI Video error:', e);
        isInitialStreamSetupRef.current = false;
        console.log('❌ Video error, flag cleared');
      };
    }
    // Cleanup on unmount or when streaming stops
    return () => {
      if (!isStreaming && videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [isStreaming, aiAnalysisEnabled, aiAnalysisInterval]);

  // useEffect to draw the streamPreview image to the canvas whenever it changes
  React.useEffect(() => {
    if (isStreaming && streamPreview && staticPreviewCanvasRef.current) {
      const img = new window.Image();
      img.onload = () => {
        const canvas = staticPreviewCanvasRef.current;
        if (!canvas) return; // Prevent error if canvas is unmounted
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.drawImage(img, 0, 0);
      };
      img.src = streamPreview;
    }
  }, [isStreaming, streamPreview]);

  // Move updateVolume function to top-level
  function updateMicVolume(analyser: AnalyserNode, dataArray: Uint8Array, setMicVolume: (v: number) => void) {
    if (!micVolumeRunningRef.current) return;
    analyser.getByteTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const val = (dataArray[i] - 128) / 128;
      sum += val * val;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    setMicVolume(rms);
    micVolumeAnimationRef.current = requestAnimationFrame(() => updateMicVolume(analyser, dataArray, setMicVolume));
  }

  // Handler to start/stop mic streaming
  const handleMicStream = async () => {
    if (isMicStreaming) {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (micRecorderRef.current) {
        if (micRecorderRef.current.state !== 'inactive') {
          micRecorderRef.current.stop();
        }
        micRecorderRef.current = null;
      }
      if (micRecordingIntervalRef.current) {
        clearInterval(micRecordingIntervalRef.current);
        micRecordingIntervalRef.current = null;
      }
      if (micSocketRef.current) {
        micSocketRef.current.disconnect();
        micSocketRef.current = null;
      }
      setIsMicStreaming(false);
      setMicVolume(0);
      micVolumeRunningRef.current = false;
      if (micVolumeAnimationRef.current) cancelAnimationFrame(micVolumeAnimationRef.current);
      enqueueSnackbar('Audio streaming stopped', { variant: 'info', autoHideDuration: 2000 });
      return;
    }
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      // Connect to WebSocket for audio streaming
      const audioSocket = io(WS_NAMESPACES.LIVE_SCREEN, {
        auth: { token: jwt },
        transports: ['websocket'],
      });
      micSocketRef.current = audioSocket;

      // Handle WebSocket events
      audioSocket.on('connect', () => {
        console.log('Audio WebSocket connected');
      });

      audioSocket.on('connect_error', (error) => {
        console.error('Audio WebSocket connection error:', error);
        enqueueSnackbar('Failed to connect audio stream: ' + error.message, { variant: 'error' });
        setIsMicStreaming(false);
        setMicVolume(0);
      });

      audioSocket.on('error', (error) => {
        console.error('Audio WebSocket error:', error);
        enqueueSnackbar('Audio stream error: ' + (error?.error || error), { variant: 'error' });
      });

      // Handle audio transcription results
      audioSocket.on('audio_transcription', (data: {
        sessionId: string;
        transcription: string;
        confidence: number;
        language: string;
        timestamp: number;
      }) => {
        console.log('Audio transcribed:', data.transcription, 'confidence:', data.confidence);
        if (data.transcription && data.confidence > 0.5) {
          setTranscriptions((prev) => [...prev, {
            text: data.transcription,
            confidence: data.confidence,
            timestamp: data.timestamp
          }]);
        }
      });

      // Handle audio analysis results
      audioSocket.on('audio_analysis', (data: {
        sessionId: string;
        analysis: {
          analysis: string;
          insights: string[];
          actions: string[];
          confidence: number;
        };
        timestamp: number;
      }) => {
        console.log('Audio analyzed:', data.analysis);
        if (data.analysis.confidence > 0.7) {
          setAudioAnalysis((prev) => [...prev, {
            analysis: data.analysis.analysis,
            insights: data.analysis.insights,
            actions: data.analysis.actions,
            confidence: data.analysis.confidence,
            timestamp: data.timestamp
          }]);
        }
      });

      // Create a session for audio streaming
      audioSocket.emit('create_session', { name: 'audio_stream_' + Date.now() });
      
      let sessionId: string | null = null;
      audioSocket.on('session_created', (data: { sessionId: string }) => {
        sessionId = data.sessionId;
        console.log('Audio session created:', sessionId);
        enqueueSnackbar('Audio streaming started!', { variant: 'success', autoHideDuration: 2000 });
      });

      let stream: MediaStream | null = null;
      let visStream: MediaStream | null = null;
      
      try {
        if (audioSource === 'mic') {
          console.log('Requesting microphone permission...');
          stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            } 
          });
          console.log('Microphone permission granted, tracks:', stream?.getAudioTracks().length);
          visStream = stream;
        } else if (audioSource === 'system') {
          console.log('Requesting system audio permission...');
          stream = await (navigator.mediaDevices as any).getDisplayMedia({ audio: true });
          console.log('System audio permission granted, tracks:', stream?.getAudioTracks().length);
          visStream = stream;
        } else if (audioSource === 'both') {
          console.log('Requesting both microphone and system audio permissions...');
          const micStream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            } 
          });
          const sysStream = await (navigator.mediaDevices as any).getDisplayMedia({ audio: true });
          stream = new MediaStream([
            ...micStream.getAudioTracks(),
            ...sysStream.getAudioTracks()
          ]);
          console.log('Both permissions granted, total tracks:', stream?.getAudioTracks().length);
          // Prefer mic track for visualization if available
          visStream = micStream.getAudioTracks().length > 0 ? new MediaStream([micStream.getAudioTracks()[0]]) : sysStream;
        }
      } catch (permissionError: any) {
        console.error('Permission request failed:', permissionError);
        if (permissionError.name === 'NotAllowedError') {
          enqueueSnackbar('Microphone permission denied. Please allow microphone access and try again.', { variant: 'error' });
        } else if (permissionError.name === 'NotFoundError') {
          enqueueSnackbar('No microphone found. Please check your audio devices.', { variant: 'error' });
        } else if (permissionError.name === 'NotReadableError') {
          enqueueSnackbar('Microphone is already in use by another application.', { variant: 'error' });
        } else {
          enqueueSnackbar('Failed to access microphone: ' + permissionError.message, { variant: 'error' });
        }
        setIsMicStreaming(false);
        setMicVolume(0);
        return;
      }
      
      if (!stream || stream.getAudioTracks().length === 0) {
        enqueueSnackbar('No valid audio track found for visualization.', { variant: 'error' });
        setIsMicStreaming(false);
        setMicVolume(0);
        return;
      }
      
      micStreamRef.current = stream;
      setIsMicStreaming(true);
      
      // Web Audio API setup for visualization
      if (visStream && visStream.getAudioTracks().length > 0) {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(visStream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        micVolumeRunningRef.current = true;
        updateMicVolume(analyser, dataArray, setMicVolume);
        // Cleanup on stop
        visStream.getAudioTracks()[0].onended = () => {
          micVolumeRunningRef.current = false;
          if (micVolumeAnimationRef.current) cancelAnimationFrame(micVolumeAnimationRef.current);
          audioCtx.close();
          setMicVolume(0);
        };
      } else {
        setMicVolume(0);
      }
      
      // MediaRecorder for streaming audio to backend
      try {
        // Use browser's default format - let it choose the best supported format
        micRecorderRef.current = new MediaRecorder(stream!);
        
        // Buffer to collect audio chunks
        const audioChunks: Blob[] = [];
        
        micRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
          }
        };
        
        micRecorderRef.current.onstop = () => {
          if (audioChunks.length > 0 && sessionId && micSocketRef.current) {
            // Create a complete audio blob from all chunks
            const audioBlob = new Blob(audioChunks, { type: audioChunks[0].type });
            console.log('Complete audio blob created, size:', audioBlob.size, 'type:', audioBlob.type);
            
            // Send the complete audio blob
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              console.log('FileReader result length:', result.length, 'starts with:', result.substring(0, 50));
              
              // More robust base64 extraction
              let base64Audio = '';
              if (result.startsWith('data:')) {
                // Handle data URL format - find the last comma for complex MIME types
                const lastCommaIndex = result.lastIndexOf(',');
                if (lastCommaIndex !== -1) {
                  base64Audio = result.substring(lastCommaIndex + 1);
                }
              } else {
                // Handle raw base64
                base64Audio = result;
              }
              
              console.log('Base64 audio length:', base64Audio?.length, 'starts with:', base64Audio?.substring(0, 20));
              
              if (base64Audio && base64Audio.length > 10) {
                // Extract the actual format from the MIME type
                let format = 'webm'; // default
                if (audioBlob.type) {
                  if (audioBlob.type.includes('webm')) {
                    format = 'webm';
                  } else if (audioBlob.type.includes('mp4') || audioBlob.type.includes('matroska')) {
                    format = 'mp4';
                  } else if (audioBlob.type.includes('wav')) {
                    format = 'wav';
                  } else if (audioBlob.type.includes('mp3')) {
                    format = 'mp3';
                  }
                }
                
                console.log('Detected audio format:', format, 'from MIME type:', audioBlob.type);
                
                micSocketRef.current?.emit('audio_chunk', {
                  sessionId,
                  audioData: base64Audio,
                  format: format // Use the detected format
                });
              } else {
                console.warn('Audio data too small or invalid, skipping:', base64Audio?.length);
              }
            };
            reader.readAsDataURL(audioBlob);
          }
          
          // Clear the buffer for next recording
          audioChunks.length = 0;
        };
        
        // Start recording without timeslice - collect complete chunks
        micRecorderRef.current.start();
        
        // Stop and restart recording every 2 seconds to send complete chunks
        const recordingInterval = setInterval(() => {
          if (micRecorderRef.current && micRecorderRef.current.state === 'recording') {
            micRecorderRef.current.stop();
            // Restart immediately
            setTimeout(() => {
              if (micRecorderRef.current && micRecorderRef.current.state === 'inactive') {
                micRecorderRef.current.start();
              }
            }, 100);
          }
        }, 2000);
        
        // Store the interval for cleanup
        micRecordingIntervalRef.current = recordingInterval;
        
        console.log('MediaRecorder started successfully with complete chunk collection');
      } catch (err) {
        console.error('MediaRecorder error:', err);
        // Don't fail the entire mic stream if MediaRecorder fails
        // Just log the error and continue with audio visualization only
        console.warn('MediaRecorder failed to start, continuing with audio visualization only');
        micRecorderRef.current = null;
      }
    } catch (err: any) {
      enqueueSnackbar('Failed to access audio: ' + (err.message || err), { variant: 'error' });
      setIsMicStreaming(false);
      setMicVolume(0);
      micVolumeRunningRef.current = false;
      if (micVolumeAnimationRef.current) cancelAnimationFrame(micVolumeAnimationRef.current);
    }
  };

  const clearTranscriptions = () => {
    setTranscriptions([]);
  };

  const clearAudioAnalysis = () => {
    setAudioAnalysis([]);
  };

  // Transcription toggle handler
  const handleToggleTranscription = () => {
    setShowTranscription(prev => !prev);
  };

  // Function to enumerate available webcam devices
  const enumerateWebcams = async () => {
    try {
      // Request permission first
      await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Get all video input devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      setAvailableWebcams(videoDevices);
      
      // Set the first webcam as default if none selected
      if (videoDevices.length > 0 && !selectedWebcamId) {
        setSelectedWebcamId(videoDevices[0].deviceId);
      }
      
      return videoDevices;
    } catch (error) {
      console.error('Failed to enumerate webcams:', error);
      enqueueSnackbar('Failed to access webcam devices. Please check permissions.', { variant: 'error' });
      return [];
    }
  };

  // Enumerate webcams on component mount
  useEffect(() => {
    enumerateWebcams();
  }, []);

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = () => {
      enumerateWebcams();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  // Restart webcam stream if device changes while streaming
  useEffect(() => {
    if (isWebcamStreaming) {
      console.log('🔄 Webcam device switching triggered for Start Webcam button:', selectedWebcamId);
      setIsWebcamDeviceSwitching(true);
      (async () => {
        try {
          // Stop current stream
          if (webcamStreamRef.current) {
            console.log('🛑 Stopping current webcam stream tracks');
            webcamStreamRef.current.getTracks().forEach(track => track.stop());
            webcamStreamRef.current = null;
          }
          
          console.log('📹 Requesting new webcam stream with device:', selectedWebcamId);
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              deviceId: selectedWebcamId ? { exact: selectedWebcamId } : undefined
            }
          });
          
          console.log('✅ New webcam stream obtained:', stream.getTracks().length, 'tracks');
          webcamStreamRef.current = stream;
          
          // If video source is set to webcam, also update the main video stream
          if (videoSource === 'webcam') {
            // Stop current main stream if it exists
            if (streamRef.current) {
              streamRef.current.getTracks().forEach(track => track.stop());
            }
            streamRef.current = stream;
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              videoRef.current.play();
            }
          }
          
          enqueueSnackbar('Webcam device switched successfully', { variant: 'success' });
          setIsWebcamDeviceSwitching(false);
          
        } catch (err: any) {
          console.error('❌ Failed to switch webcam device:', err);
          enqueueSnackbar('Failed to switch webcam: ' + (err.message || err), { variant: 'error' });
          setIsWebcamStreaming(false);
          setIsWebcamDeviceSwitching(false);
        }
      })();
    }
  }, [selectedWebcamId]);

  // Switch video source during active live stream
  useEffect(() => {
    if (isStreaming && streamRef.current && videoRef.current && !isInitialStreamSetupRef.current) {
      console.log('🔄 Source switching triggered:', { videoSource, selectedWebcamId });
      setIsSourceSwitching(true);
      (async () => {
        try {
          // Stop current stream tracks
          if (streamRef.current) {
            console.log('🛑 Stopping current stream tracks');
            streamRef.current.getTracks().forEach(track => track.stop());
          }
          
          // Get new stream based on current video source
          let newStream: MediaStream;
          
          if (videoSource === 'screen') {
            console.log('📺 Requesting screen capture');
            // Screen capture
            newStream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
          } else {
            console.log('📹 Requesting webcam capture with device:', selectedWebcamId);
            // Webcam capture
            newStream = await navigator.mediaDevices.getUserMedia({ 
              video: { 
                width: { ideal: 1280 },
                height: { ideal: 720 },
                deviceId: selectedWebcamId ? { exact: selectedWebcamId } : undefined
              } 
            });
          }
          
          console.log('✅ New stream obtained:', newStream.getTracks().length, 'tracks');
          
          // Update stream reference
          streamRef.current = newStream;
          
          // Update video element
          if (videoRef.current) {
            videoRef.current.srcObject = newStream;
            
            // Wait for video to be ready and restart streaming
            videoRef.current.onloadedmetadata = () => {
              console.log('🎥 Video source switched, videoWidth:', videoRef.current?.videoWidth, 'videoHeight:', videoRef.current?.videoHeight);
              videoRef.current?.play();
              
              // Restart streaming with new video source
              const jwt = getJwtFromCookie() || '';
              console.log('🔄 Restarting streaming with new source');
              startStreamingWithVideo({
                videoEl: videoRef.current!,
                jwt,
                aiAnalysisEnabled,
                aiAnalysisInterval,
                enqueueSnackbar,
                setLiveSessionId,
                setStreamPreview,
                setAiAnalysisResult,
                setIsStreaming,
                streamIntervalRef,
                aiAnalysisIntervalRef
              });
              
              // Clear switching state
              setIsSourceSwitching(false);
            };
          }
          
          enqueueSnackbar(`Switched to ${videoSource} source`, { variant: 'success' });
          
        } catch (err: any) {
          console.error('❌ Failed to switch video source:', err);
          enqueueSnackbar('Failed to switch video source: ' + (err.message || err), { variant: 'error' });
          // If switching fails, stop streaming
          setIsStreaming(false);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          setIsSourceSwitching(false);
        }
      })();
    }
  }, [videoSource, selectedWebcamId]); // Only trigger when video source or webcam device changes

  // Global Voice Command Handler
  const handleGlobalVoiceCommand = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      enqueueSnackbar('Speech recognition is not supported in this browser.', { variant: 'error' });
      return;
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      enqueueSnackbar('Listening for command...', { variant: 'info' });
    };
    recognition.onerror = (event: any) => {
      enqueueSnackbar('Voice command error: ' + event.error, { variant: 'error' });
    };
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.trim().toLowerCase();
      let matched = false;
      // Define supported commands
      if (transcript.includes('start streaming')) {
        handleLiveStream();
        enqueueSnackbar('Started live streaming.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('stop streaming')) {
        if (isStreaming) handleLiveStream();
        enqueueSnackbar('Stopped live streaming.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('switch to webcam')) {
        setVideoSource('webcam');
        enqueueSnackbar('Switched to webcam.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('switch to screen')) {
        setVideoSource('screen');
        enqueueSnackbar('Switched to screen.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('open settings')) {
        setShowSettings(true);
        enqueueSnackbar('Opened settings.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('toggle dark mode') || transcript.includes('toggle theme')) {
        handleThemeToggle();
        enqueueSnackbar('Toggled theme.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('show ai response')) {
        setShowAIResponse(true);
        enqueueSnackbar('AI response panel shown.', { variant: 'success' });
        matched = true;
      } else if (transcript.includes('hide ai response')) {
        setShowAIResponse(false);
        enqueueSnackbar('AI response panel hidden.', { variant: 'success' });
        matched = true;
      }
      if (!matched) {
        enqueueSnackbar(`Unrecognized command: "${transcript}"`, { variant: 'warning' });
      }
    };
    recognition.start();
  };

  // Toolbar orientation and side handlers
  const handleToggleToolbarOrientation = async () => {
    let newOrientation: 'horizontal' | 'vertical' | 'bottom';
    if (toolbarOrientation === 'horizontal') {
      newOrientation = 'vertical';
    } else if (toolbarOrientation === 'vertical') {
      newOrientation = 'bottom';
    } else {
      newOrientation = 'horizontal';
    }
    setToolbarOrientation(newOrientation);
    // Save preference to user settings
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) return;
      const response = await fetch(ENDPOINTS.USERS_SETTINGS, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toolbarLayout: newOrientation
        }),
      });
      if (!response.ok) {
        console.error('Failed to save toolbar layout preference');
      }
    } catch (error) {
      console.error('Failed to save toolbar layout preference:', error);
    }
  };

  const handleToggleToolbarSide = () => {
    setToolbarSide((prev) => (prev === 'left' ? 'right' : 'left'));
  };

  // Ref to track the webcam video element
  const webcamVideoRef = React.useRef<HTMLVideoElement | null>(null);
  
  // Effect to update webcam video element when stream changes
  useEffect(() => {
    if (webcamVideoRef.current && webcamStreamRef.current) {
      console.log('🎥 Updating webcam video element with new stream');
      webcamVideoRef.current.srcObject = webcamStreamRef.current;
    }
  }, [webcamStreamRef.current]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jwt = params.get('jwt');
    if (jwt) {
      localStorage.setItem('jwt', jwt);
      // Remove the jwt param from the URL
      params.delete('jwt');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
      window.location.reload(); // Optional: reload to trigger auth state
    }
  }, []);

  // Sidebar API functions
  const fetchConversations = async () => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated()) {
        console.log('User not authenticated, redirecting to auth');
        redirectToAuth();
        return;
      }

      const jwt = getJwtFromCookie();
      if (!jwt) {
        console.log('No JWT found, redirecting to auth');
        redirectToAuth();
        return;
      }

      dispatch(setConversationsLoading(true));
      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}?limit=50`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.status === 401) {
        console.log('Unauthorized, redirecting to auth');
        redirectToAuth();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          dispatch(setConversations(data.data.conversations));
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      dispatch(setConversationsError('Failed to load conversations'));
      // On error, redirect to auth
      redirectToAuth();
    } finally {
      dispatch(setConversationsLoading(false));
    }
  };

  const createNewConversation = async (type: 'general' | 'screenshare') => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(ENDPOINTS.CONVERSATIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          title: type === 'screenshare' ? 'Screen Share Session' : 'General Chat',
          type,
        }),
        credentials: 'include',
      });

      const data = await response.json();
      if (data.success) {
        dispatch(addConversation(data.data));
        dispatch(setActiveConversation(data.data._id));
        return data.data._id;
      } else {
        throw new Error(data.error || 'Failed to create conversation');
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      enqueueSnackbar('Failed to create conversation', { variant: 'error' });
    }
  };

  const pinConversation = async (conversationId: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/${conversationId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        credentials: 'include',
      });

      const data = await response.json();
      if (data.success) {
        dispatch(updateConversation(data.data));
      }
    } catch (error) {
      console.error('Failed to pin conversation:', error);
      enqueueSnackbar('Failed to pin conversation', { variant: 'error' });
    }
  };

  const unpinConversation = async (conversationId: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/${conversationId}/unpin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        credentials: 'include',
      });

      const data = await response.json();
      if (data.success) {
        dispatch(updateConversation(data.data));
      }
    } catch (error) {
      console.error('Failed to unpin conversation:', error);
      enqueueSnackbar('Failed to unpin conversation', { variant: 'error' });
    }
  };

  const renameConversation = async (conversationId: string, newTitle: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ title: newTitle }),
      });

      const data = await response.json();
      if (data.success) {
        dispatch(updateConversation(data.data));
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      enqueueSnackbar('Failed to rename conversation', { variant: 'error' });
    }
  };

  const deleteConversation = async (conversationId: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        dispatch(removeConversation(conversationId));
        enqueueSnackbar('Conversation deleted', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      enqueueSnackbar('Failed to delete conversation', { variant: 'error' });
    }
  };

  const archiveConversation = async (conversationId: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) throw new Error('No JWT token found');

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/${conversationId}/archive`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        // Refresh conversations to get updated status
        await fetchConversations();
        enqueueSnackbar('Conversation archived', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      enqueueSnackbar('Failed to archive conversation', { variant: 'error' });
    }
  };

  const searchConversations = async (query: string) => {
    try {
      const jwt = getJwtFromCookie();
      if (!jwt) return;

      if (!query.trim()) {
        dispatch(setSearchResults([]));
        return;
      }

      const response = await fetch(`${ENDPOINTS.CONVERSATIONS}/search?q=${encodeURIComponent(query)}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const searchResultIds = data.data.conversations.map((conv: any) => conv._id);
          dispatch(setSearchResults(searchResultIds));
        }
      }
    } catch (error) {
      console.error('Failed to search conversations:', error);
    }
  };

  // Load conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Responsive breakpoints
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarSwitching, setSidebarSwitching] = useState(false);
  const [showConversationPanel, setShowConversationPanel] = useState(false);

  // Real-time sidebar updates via WebSocket
  useEffect(() => {
    const jwt = getJwtFromCookie();
    if (!jwt) return;
    const socket = io(WS_NAMESPACES.CONVERSATIONS, {
      transports: ['websocket'],
      auth: { token: jwt },
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to /conversations WebSocket');
    });
    socket.on('disconnect', () => {
      console.log('Disconnected from /conversations WebSocket');
    });

    socket.on('conversation:created', (data) => {
      dispatch(addConversation(data));
    });
    socket.on('conversation:updated', (data) => {
      dispatch(updateConversation(data));
    });
    socket.on('conversation:deleted', (id) => {
      dispatch(removeConversation(id));
    });
    socket.on('conversation:pinned', (data) => {
      dispatch(updateConversation(data));
    });
    socket.on('conversation:unpinned', (data) => {
      dispatch(updateConversation(data));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch]);

  return (
    <RequireAuth>
      {/* Analysis Status Bar */}
      {activeAnalysis && showStatusBar && (
        <AnalysisStatusBar onClose={() => setShowStatusBar(false)} />
      )}
      
      <Box sx={{ position: 'relative', width: '100vw', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Sidebar Drawer */}
        {!sidebarSwitching && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: 320, maxWidth: '90vw', boxSizing: 'border-box' } }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <TopActions 
                  onSearch={searchConversations}
                  searchInputRef={searchInputRef}
                />
                <ConversationHistoryList 
                  onPin={pinConversation}
                  onUnpin={unpinConversation}
                  onRename={renameConversation}
                  onDelete={deleteConversation}
                  onArchive={archiveConversation}
                                  onSelect={(id: string) => {
                  console.log('Conversation selected:', id);
                  dispatch(setActiveConversation(id));
                  setShowConversationPanel(true);
                }}
                />
                <BottomSection 
                  onOpenSettings={handleOpenSettings}
                  onLogout={() => {
                    localStorage.removeItem('jwt');
                    window.location.reload();
                  }}
                />
              </Box>
            </Box>
          </Drawer>
        )}
        {/* Sidebar Toggle Button - only show when sidebar is closed */}
        {!sidebarOpen && (
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 1301, // above Drawer
              bgcolor: 'background.paper',
              boxShadow: 2,
            }}
            aria-label="Open sidebar"
            size="large"
          >
            <MenuIcon />
          </IconButton>
        )}
        {/* Main Content */}
        <Box
          sx={{
            minHeight: '100vh',
            p: isMobile ? 0.5 : isTablet ? 1 : 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 2,
          }}
          tabIndex={0}
          aria-label="Main application area"
        >
          <Toolbar 
            onThemeToggle={handleThemeToggle} 
            darkMode={themeMode === 'dark'} 
            onOpenSettings={handleOpenSettings} 
            onToggleAIResponse={() => setShowAIResponse((v) => !v)}
            showAIResponse={showAIResponse}
            onScreenCapture={videoSource === 'screen' ? handleScreenCapture : handleWebcamCapture}
            onLiveStream={handleLiveStream}
            isStreaming={isStreaming}
            isSourceSwitching={isSourceSwitching}
            onMicStream={handleMicStream}
            isMicStreaming={isMicStreaming}
            micVolume={micVolume}
            audioSource={audioSource}
            onAudioSourceChange={setAudioSource}
            onToggleTranscription={handleToggleTranscription}
            showTranscription={showTranscription}
            videoSource={videoSource}
            onVideoSourceChange={setVideoSource}
            onWebcamStream={handleWebcamStream}
            isWebcamStreaming={isWebcamStreaming}
            isWebcamDeviceSwitching={isWebcamDeviceSwitching}
            availableWebcams={availableWebcams}
            selectedWebcamId={selectedWebcamId}
            onWebcamDeviceChange={setSelectedWebcamId}
            onGlobalVoiceCommand={handleGlobalVoiceCommand}
            toolbarOrientation={toolbarOrientation}
            toolbarSide={toolbarSide}
            onToggleToolbarOrientation={handleToggleToolbarOrientation}
            onToggleToolbarSide={handleToggleToolbarSide}
          />
          {showSettings ? (
            <SettingsPage onClose={handleCloseSettings} />
          ) : (
            <>
              <Box sx={{ 
                width: '100%', 
                maxWidth: isMobile ? 360 : isTablet ? 600 : 800,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                p: 2,
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              }}>
                {/* Show capture/streaming content first in all orientations */}
                {(toolbarOrientation === 'horizontal' || toolbarOrientation === 'bottom' || toolbarOrientation === 'vertical') && (
                  <Box sx={{ mt: 10 }}>
                    {isStreaming && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        mb: 1, 
                        display: 'flex', 
                        flexDirection: 'row', 
                        justifyContent: 'center', 
                        alignItems: 'flex-start', 
                        gap: 3,
                        background: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Live Video (what you are sharing)</Typography>
                          <video ref={videoRef} autoPlay muted playsInline style={{ maxWidth: 400, maxHeight: 200, borderRadius: 8, margin: '8px 0', background: '#222' }} />
                        </Box>
                        {streamPreview && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Static Preview (last frame sent to backend)</Typography>
                            <canvas ref={staticPreviewCanvasRef} style={{ maxWidth: 400, maxHeight: 200, borderRadius: 8, margin: '8px 0', background: '#222', width: '100%' }} />
                          </Box>
                        )}
                      </Box>
                    )}
                    {/* Webcam Preview Section */}
                    {isWebcamStreaming && webcamStreamRef.current && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        mb: 1, 
                        display: 'flex', 
                        flexDirection: 'row', 
                        justifyContent: 'center', 
                        alignItems: 'flex-start', 
                        gap: 3,
                        background: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Webcam Stream</Typography>
                          <video 
                            ref={webcamVideoRef}
                            autoPlay 
                            muted 
                            playsInline 
                            style={{ maxWidth: 400, maxHeight: 200, borderRadius: 8, margin: '8px 0', background: '#222' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Webcam Status</Typography>
                          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                            ✓ Webcam Active
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            Resolution: 1280x720
                          </Typography>
                          {availableWebcams.length > 1 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                              Device: {availableWebcams.find(w => w.deviceId === selectedWebcamId)?.label || 'Unknown'}
                            </Typography>
                          )}
                          <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                            🎯 Ready for AI Analysis
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            Type a question to analyze what the webcam sees
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {/* Screen Capture Preview Section */}
                    {screenImage && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        mb: 1, 
                        display: 'flex', 
                        flexDirection: 'row', 
                        justifyContent: 'center', 
                        alignItems: 'flex-start', 
                        gap: 3, 
                        position: 'relative',
                        background: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Box sx={{ position: 'relative' }}>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Captured {videoSource === 'screen' ? 'Screen' : 'Webcam'}</Typography>
                          <img 
                            src={screenImage} 
                            alt="Captured content"
                            style={{ maxWidth: 400, maxHeight: 200, borderRadius: 8, margin: '8px 0', objectFit: 'contain', background: '#222' }}
                          />
                          <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.4)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }} onClick={() => setScreenImage(null)} aria-label="Clear captured image">
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>Capture Status</Typography>
                          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                            ✓ {videoSource === 'screen' ? 'Screen' : 'Webcam'} Captured
                          </Typography>
                          <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                            🎯 Ready for AI Analysis
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            Type a question to analyze this {videoSource === 'screen' ? 'screen' : 'webcam'} capture
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                {/* MainInput now comes after the capture/preview windows */}
                {(toolbarOrientation === 'horizontal' || toolbarOrientation === 'bottom' || toolbarOrientation === 'vertical') && (
                  <MainInput onSubmit={handleSubmit} loading={loading} />
                )}
                {/* Conversation Panel - show only when explicitly selected from sidebar */}
                {showConversationPanel && activeConversationId && <ConversationPanel />}
                {showAIResponse && (
                  <AIResponsePanel 
                    responses={responses} 
                    loading={loading}
                  />
                )}
                {showTranscription && (
                  <AudioPanel
                    transcriptions={transcriptions}
                    audioAnalysis={audioAnalysis}
                    onClearTranscriptions={clearTranscriptions}
                    onClearAnalysis={clearAudioAnalysis}
                  />
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Property Analysis Panel */}
        <PropertyAnalysisPanel />
        
        {/* Floating Progress Widget */}
        {activeAnalysis && showFloatingWidget && (
          <FloatingProgressWidget onClose={() => setShowFloatingWidget(false)} />
        )}
        
        {/* Live Activity Feed */}
        {activeAnalysis && showActivityFeed && (
          <LiveActivityFeed onClose={() => setShowActivityFeed(false)} />
        )}
        
        {/* Trading Progress Modal */}
        <TradingProgressModal />
        
        {/* Trading FAB */}
        <TradingFAB />
      </Box>
    </RequireAuth>
  );
};

export default App; 